import React from "react";
import {
  Card,
  Space,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import {
  useInventoryReportsServiceDownloadWarehouseStockReport,
  useInventoryReportsServiceGetWarehouseStockReport,
} from "api/queries";

const { Title } = Typography;

const WarehouseStockReport = () => {
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
  });

  const { data, isLoading } = useInventoryReportsServiceGetWarehouseStockReport(
    {
      limit: pagination.pageSize,
      offset: (pagination.current - 1) * pagination.pageSize,
      isActive: true,
    }
  );

  const { mutate: downloadReport, isPending } =
    useInventoryReportsServiceDownloadWarehouseStockReport();

  // KPIs
  const kpis = data?.kpis;
  // Table data
  const locations =
    data?.locations?.map((item: any) => ({
      key: item.locationId,
      locationName: item.locationName,
      locationType: item.locationType,
      totalProducts: item.totalProducts,
      totalQuantity: item.totalQuantity,
    })) || [];

  const columns = [
    {
      title: "Location Name",
      dataIndex: "locationName",
      key: "locationName",
    },
    {
      title: "Type",
      dataIndex: "locationType",
      key: "locationType",
    },
    {
      title: "Total Products",
      dataIndex: "totalProducts",
      key: "totalProducts",
    },
    {
      title: "Total Quantity",
      dataIndex: "totalQuantity",
      key: "totalQuantity",
    },
  ];

  const handleTableChange = (paginationConfig: any) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const handleDownload = () => {
    downloadReport(
      {
        isActive: true,
      },
      {
        onSuccess: (response: {
          success: boolean;
          message: string;
          downloadUrl?: string;
        }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "warehouse_stock_report.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <div style={{ padding: "24px" }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Title level={2}>Warehouse Stock Levels Report</Title>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={isPending}
                onClick={handleDownload}
              >
                Download Report
              </Button>
            </Space>

            {/* Summary Statistics */}
            <Row gutter={16}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Locations"
                    value={kpis?.totalLocations || 0}
                    loading={isLoading}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Active Products"
                    value={kpis?.totalActiveProducts || 0}
                    loading={isLoading}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Quantity"
                    value={kpis?.totalQuantity || 0}
                    loading={isLoading}
                  />
                </Card>
              </Col>
            </Row>

            {/* Main Data Table */}
            <Table
              columns={columns}
              dataSource={locations}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: data?.pagination?.total || 0,
                showSizeChanger: true,
              }}
              loading={isLoading}
              scroll={{ x: "max-content" }}
              onChange={handleTableChange}
            />
          </Space>
        </Card>
      </div>
    </motion.div>
  );
};

export default WarehouseStockReport;
